/* eslint-disable max-statements */
/**
 * Validates if a string is a valid hex color
 * @param color - The color string to validate
 * @returns True if valid hex color, false otherwise
 */
const isValidHexColor = (color: string): boolean => /^#?(?:[A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/u.test(color);

/**
 * Normalizes a hex color to 6-digit format with # prefix
 * @param color - The hex color string (e.g., '#00453a', '00453a', '#fff', 'fff')
 * @returns The normalized 6-digit hex color string with # prefix
 */
const normalizeHexColor = (color: string): string => {
    if (!isValidHexColor(color)) {
        throw new Error(`Invalid hex color: ${color}`);
    }

    let hex = color.replace('#', '');

    // Expand 3-digit hex to 6-digit
    if (hex.length === 3) {
        hex = hex.split('')
            .map(char => char + char)
            .join('');
    }

    return `#${hex.toLowerCase()}`;
};

/**
 * Lightens a hex color by a given percentage
 * @param color - The hex color string (e.g., '#00453a', '00453a', '#fff')
 * @param percent - The percentage to lighten (0 to 1, where 0.85 = 85% lighter)
 * @returns The lightened hex color string
 * @throws Error if color is invalid or percent is out of range
 */
export const lightenColor = (color: string, percent: number): string => {
    if (percent < 0 || percent > 1) {
        throw new Error(`Percent must be between 0 and 1, got ${percent}`);
    }

    const normalizedColor = normalizeHexColor(color);
    const hex = normalizedColor.substring(1);

    const red = parseInt(hex.substring(0, 2), 16);
    const green = parseInt(hex.substring(2, 4), 16);
    const blue = parseInt(hex.substring(4, 6), 16);

    const newR = Math.min(255, Math.floor(red + ((255 - red) * percent)));
    const newG = Math.min(255, Math.floor(green + ((255 - green) * percent)));
    const newB = Math.min(255, Math.floor(blue + ((255 - blue) * percent)));

    return `#${newR.toString(16)
        .padStart(2, '0')}${newG.toString(16)
        .padStart(2, '0')}${newB.toString(16)
        .padStart(2, '0')}`;
};

/**
 * Calculates the relative luminance of a color based on the WCAG formula
 * @param color - The hex color string (e.g., '#00453a', '00453a', '#fff')
 * @returns The relative luminance value (0-1)
 * @throws Error if color is invalid
 */
export const getRelativeLuminance = (color: string): number => {
    const normalizedColor = normalizeHexColor(color);
    const hex = normalizedColor.substring(1);

    const red = parseInt(hex.substring(0, 2), 16) / 255;
    const green = parseInt(hex.substring(2, 4), 16) / 255;
    const blue = parseInt(hex.substring(4, 6), 16) / 255;

    // Apply gamma correction
    const toLinear = (channel: number): number =>
        channel <= 0.03928 ? channel / 12.92 : ((channel + 0.055) / 1.055)**2.4;

    const rLinear = toLinear(red);
    const gLinear = toLinear(green);
    const bLinear = toLinear(blue);

    // Calculate relative luminance
    return (0.2126 * rLinear) + (0.7152 * gLinear) + (0.0722 * bLinear);
};

/**
 * Determines if a color is considered light or dark
 * @param color - The hex color string (e.g., '#00453a', '00453a', '#fff')
 * @param threshold - The luminance threshold (default: 0.179, WCAG recommended)
 * @returns True if the color is light, false if dark
 * @throws Error if color is invalid or threshold is out of range
 */
export const isLightColor = (color: string, threshold = 0.179): boolean => {
    if (threshold < 0 || threshold > 1) {
        throw new Error(`Threshold must be between 0 and 1, got ${threshold}`);
    }

    return getRelativeLuminance(color) > threshold;
};

/**
 * Gets the appropriate text color (black or white) for a given background color
 * @param backgroundColor - The hex background color string (e.g., '#00453a', '00453a', '#fff')
 * @returns '#000000' for light backgrounds, '#ffffff' for dark backgrounds
 * @throws Error if backgroundColor is invalid
 */
export const getContrastTextColor = (backgroundColor: string): string =>

    // Using WCAG recommended threshold for text contrast
    isLightColor(backgroundColor, 0.179) ? '#000000' : '#ffffff';
